/**
 * Cashflow Analysis - Utility Functions
 */

import moment from "moment";

/**
 * Format a number as USD currency string.
 */
export function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

/**
 * Format a number as USD currency with cents.
 */
export function formatCurrencyExact(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

/**
 * Format a percentage (0.30 -> "30%").
 */
export function formatPercentage(value: number): string {
  return `${(value * 100).toFixed(0)}%`;
}

/**
 * Get a month label like "Mar 2026" from a "YYYY-MM" key.
 */
export function formatMonthLabel(monthKey: string): string {
  return moment(monthKey, 'YYYY-MM').format('MMM YYYY');
}

/**
 * Generate an array of month keys from start to end (inclusive).
 */
export function getMonthRange(startMonth: string, endMonth: string): string[] {
  const months: string[] = [];
  let current = moment(startMonth, 'YYYY-MM');
  const end = moment(endMonth, 'YYYY-MM');

  while (current.isSameOrBefore(end, 'month')) {
    months.push(current.format('YYYY-MM'));
    current = current.add(1, 'month');
  }

  return months;
}

/**
 * Get default start month (current month).
 */
export function getDefaultStartMonth(): string {
  return moment().format('YYYY-MM');
}

/**
 * Get default end month: 6 months out or end of year, whichever is later.
 */
export function getDefaultEndMonth(): string {
  const sixMonthsOut = moment().add(5, 'months');
  const endOfYear = moment().endOf('year');
  const later = sixMonthsOut.isAfter(endOfYear) ? sixMonthsOut : endOfYear;
  return later.format('YYYY-MM');
}

/**
 * Get unique suppliers from allocations.
 */
export function getUniqueSuppliers(
  months: Record<string, { bySupplier: Record<string, number> }>,
): string[] {
  const set = new Set<string>();
  for (const month of Object.values(months)) {
    for (const supplier of Object.keys(month.bySupplier)) {
      set.add(supplier);
    }
  }
  return Array.from(set).sort();
}

/**
 * Get unique brands from allocations entries.
 */
export function getUniqueBrands(
  months: Record<string, { entries: Array<{ brand: string | null }> }>,
): string[] {
  const set = new Set<string>();
  for (const month of Object.values(months)) {
    for (const entry of month.entries) {
      if (entry.brand) set.add(entry.brand);
    }
  }
  return Array.from(set).sort();
}
